// In-flight dedupe for ClientHub Supabase READS (Brian's load-perf memo §2).
//
// Two components mounting in the same tick — and React 18 StrictMode's
// double-invoke of effects in development — can fire the SAME read RPC twice
// before either settles. This collapses truly-concurrent identical reads into a
// single network round trip: the second caller awaits the first's in-flight
// promise.
//
// This is IN-FLIGHT ONLY. There is no TTL / value cache, so:
//   - a poll 60 s later always hits the network and gets fresh data;
//   - a re-fetch after a mutation (dismiss, resolve, accept) is never stale;
//   - a retry after an error is a clean second attempt.
//
// Only read wrappers call this. Writes (fn_thread_resolve, fn_compose_send,
// fn_mailbox_enable, …) must NOT be deduped — a deduped write would swallow a
// legitimate second write. The wall (RLS / the DB function) is unchanged; this
// only changes how many times the browser asks.
//
// Keys are RPC name + a stable JSON of params. Empty/undefined params normalise
// to the same key, so `fn_conversation_counts()` (sidebar, no args) and
// `fn_conversation_counts({})` (Conversations page, no clientId) share ONE
// request — the server treats them identically.

const inflight = new Map();

/**
 * Build a stable dedupe key from an RPC name and its params.
 * @param {string} name
 * @param {object} [params]
 * @returns {string}
 */
export function rpcKey(name, params) {
  const norm = params && typeof params === "object" ? params : {};
  return `${name}|${JSON.stringify(norm)}`;
}

/**
 * Run `fn` once per concurrent `key`; concurrent callers share the same
 * promise. The entry is cleared on settle (resolve OR reject) so the next call
 * is always a fresh request.
 * @param {string} key
 * @param {() => Promise<unknown>} fn
 * @returns {Promise<unknown>}
 */
export function dedupe(key, fn) {
  const pending = inflight.get(key);
  if (pending) return pending;
  // Promise.resolve().then(fn) defers fn to a microtask so a synchronous throw
  // inside fn rejects the shared promise instead of escaping to the caller.
  const p = Promise.resolve()
    .then(fn)
    .finally(() => {
      if (inflight.get(key) === p) inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}